<template>
  <Dialog
    :visible.sync="visible"
    :modal="true"
    :closable="true"
    :style="{ width: $isMobile() ? '100vw' : 'min(90vw, 1100px)' }"
    :class="{ 'p-dialog-maximized': $isMobile() }"
  >
    <template #header>
      <div class="dialog-header-wrap">
        <span class="p-dialog-title">Veículos · Reservas</span>
        <span v-if="calculatingDistance" class="p-tag p-tag-warning distance-tag">
          <i class="pi pi-spin pi-spinner p-mr-1"></i> A calcular km...
        </span>
        <span
          v-else-if="totalDistanceKm !== null"
          class="p-tag p-tag-info distance-tag"
          :title="`${distanceIsEstimate ? 'Estimativa em linha reta x1.3 (rota indisponível)' : 'Distância por estrada'} · Partida e regresso: ${baseLocation.name}`"
        >
          <i class="pi pi-car p-mr-1"></i>
          Total aprox. {{ totalDistanceKm }} km
          <small class="base-badge-note">
            ({{ baseLocation.name }} ⇄ Base{{ distanceIsEstimate ? ", estimativa" : "" }}{{ missingCoordsCount ? `, ${missingCoordsCount} sem coordenadas` : "" }})
          </small>
        </span>
        <span
          v-if="totalDistanceKm !== null"
          class="p-tag p-tag-success distance-tag"
        >
          <i class="pi pi-info-circle p-mr-1"></i>
          <p class="base-badge-note">
            <span v-if="totalDistanceKm > 270">
              Sugestão: Reservar veículo a combustão.
            </span>
            <span v-else>
              Sugestão: Reservar veículo elétrico.
            </span>
          </p>
        </span>
      </div>
    </template>
    <div class="energy-toolbar">
      <div class="energy-search">
        <label for="vehiclesEnergySearch">Pesquisar veículo</label>
        <InputText
          id="vehiclesEnergySearch"
          v-model="search"
          type="search"
          placeholder="Matrícula, VIN, marca ou modelo"
        />
      </div>
      <Button
        label="Aluguer"
        icon="pi pi-plus"
        class="p-button-outlined p-mt-4"
        :disabled="loading || savingVehicleId !== null || hasOpenReservation || date !== today"
        @click="showRental = !showRental"
      />
      <Button
        label="Atualizar"
        icon="pi pi-refresh"
        class="p-button-outlined p-mt-4"
        :disabled="loading || savingVehicleId !== null"
        @click="loadVehicles"
      />
    </div>
    <form v-if="showRental" class="energy-vehicle p-mt-3" @submit.prevent="reserveRental">
      <h3>Registar e reservar veículo de aluguer</h3>
      <div class="p-fluid p-formgrid p-grid">
        <div class="p-field p-col-12 p-md-4">
          <label for="rentalRegistration">Matrícula</label>
          <InputMask id="rentalRegistration" v-model="rental.registration" mask="**-**-**" required
            :disabled="savingVehicleId !== null" />
        </div>
        <div class="p-field p-col-12 p-md-4">
          <label for="rentalKms">Quilómetros atuais</label>
          <InputText id="rentalKms" v-model="rental.kms" type="number" required min="0" max="2147483647" step="1"
            :disabled="savingVehicleId !== null" />
        </div>
        <div class="p-field p-col-12 p-md-4">
          <label for="rentalEndDate">Data de fim do aluguer</label>
          <Calendar
            id="rentalEndDate"
            v-model="rental.rental_end_date"
            dateFormat="dd/mm/yy"
            :showIcon="true"
            :manualInput="false"
            :minDate="new Date(today + 'T00:00:00')"
            :disabled="savingVehicleId !== null"
            required
          />
        </div>
      </div>
      <Button type="submit" label="Registar e reservar" icon="pi pi-check"
        :loading="savingVehicleId === 'rental'"
        :disabled="loading || savingVehicleId !== null || hasOpenReservation || date !== today" />
    </form>
    <p class="energy-note">
      Reserva a partir de <strong>{{ selectedDateLabel }}</strong>.
      Cancelamento manual ou encerramento automático diário às 21h.
      As leituras de energia são as últimas disponíveis.
    </p>
    <Message v-if="hasOpenReservation" severity="info" :closable="false">
      Já tem uma reserva em aberto. Cancele-a para reservar outro veículo ou aguarde o encerramento diário às 21h
      após o seu início.
    </Message>
    <Message v-if="error" severity="error" :closable="false">{{ error }}</Message>
    <Message v-if="actionError" severity="error" :closable="false">{{ actionError }}</Message>
    <Message v-if="actionNotice" :severity="noticeSeverity" :closable="false">{{ actionNotice }}</Message>
    <Message v-if="failedCount" severity="warn" :closable="false">
      Não foi possível consultar {{ failedCount }} veículos.
      As restantes leituras continuam disponíveis; tente atualizar.
    </Message>
    <p role="status">
      {{ loading ? 'A carregar leituras dos veículos...' : `${rows.length} veículos nesta lista` }}
    </p>
    <p v-if="!loading && !error && !rows.length">Não existem veículos na frota local para esta pesquisa.</p>
    <div class="energy-list">
      <article v-for="row in rows" :key="row.vehicle.vehicle_id" class="energy-vehicle">
        <div class="energy-vehicle-heading">
          <div>
            <strong>{{ row.vehicle.plate || row.vehicle.vin || 'Sem matrícula' }}</strong>
            <p>
              {{ row.vehicle.brand }} {{ row.vehicle.model }} ·
              {{ row.vehicle.fuel_type || 'Combustível não definido' }}
            </p>
            <p v-if="row.vehicle.rental_end_date">
              Aluguer até {{ row.vehicle.rental_end_date.split('-').reverse().join('/') }}
            </p>
          </div>
          <Button
            v-if="row.vehicle.can_cancel"
            label="Cancelar reserva"
            icon="pi pi-times"
            class="p-button-outlined p-button-danger"
            :loading="savingVehicleId === row.vehicle.vehicle_id"
            :disabled="loading || savingVehicleId !== null"
            @click="confirmCancel(row.vehicle)"
          />
          <Button
            v-else
            label="Reservar"
            icon="pi pi-calendar-plus"
            :loading="savingVehicleId === row.vehicle.vehicle_id"
            :disabled="loading || savingVehicleId !== null || hasOpenReservation || !row.vehicle.can_reserve"
            @click="changeReservation(row.vehicle, false)"
          />
        </div>
        <p v-if="row.vehicle.reservation" class="energy-note">
          Reservado por si a partir de {{ formatTimestamp(row.vehicle.reservation.start) }}.
        </p>
        <p
          v-else
          class="availability-note"
          :class="{ 'is-reserved': ['reserved', 'reserved_later'].includes(row.vehicle.availability_status) }"
        >
          {{ availabilityLabel(row.vehicle.availability_status) }}
        </p>
        <p v-if="row.status" role="status" class="energy-note">{{ row.status }}</p>
        <div v-else class="energy-groups">
          <section v-for="(group, groupIndex) in row.groups" :key="groupIndex" class="energy-group">
            <h3>{{ group.title }}</h3>
            <div v-for="metric in group.metrics" :key="metric.path" class="energy-metric">
              <div class="energy-metric-value">
                <span>{{ metric.label }}</span><strong>{{ metric.value }}</strong>
              </div>
              <small>
                {{ metric.timestamp ? `Leitura: ${formatTimestamp(metric.timestamp)}` : 'Sem data de leitura' }}
              </small>
            </div>
          </section>
        </div>
      </article>
    </div>
    <template #footer>
      <Button label="Fechar" icon="pi pi-times" class="p-button-text" @click="visible = false" />
    </template>
  </Dialog>
</template>

<script>
import FleetService from "../../services/fleet.service";
import { reading, formatReading, formatTimestamp } from "../../helpers/targaTelematics";
import { getBaseLocation, getDayTasks, calculateRoundTrip, hasCoords } from "@/helpers/routeDistance";
import { getOnlyDate } from "../../helpers/helpers";

export default {
  name: "ShowAllVehicles",
  props: {
    show: { type: Boolean, default: false },
    date: { type: String, required: true },
    userId: { type: [Number, String], default: null },
  },
  data() {
    return {
      vehicles: [],
      search: "",
      loading: false,
      error: "",
      requestSequence: 0,
      savingVehicleId: null,
      actionError: "",
      actionNotice: "",
      noticeSeverity: "success",
      calculatingDistance: false,
      totalDistanceKm: null,
      distanceIsEstimate: false,
      missingCoordsCount: 0,
      distanceSequence: 0,
      baseLocation: null,
      today: getOnlyDate(new Date()),
      showRental: false,
      rental: { registration: "", kms: "", rental_end_date: new Date() },
    };
  },
  computed: {
    visible: {
      get() { return this.show; },
      set(value) { if (!value) this.$emit("close"); },
    },
    failedCount() { return this.vehicles.filter((vehicle) => vehicle.status === "unavailable").length; },
    hasOpenReservation() { return this.vehicles.some((vehicle) => vehicle.user_has_reservation); },
    selectedDateLabel() { return this.date.split("-").reverse().join("/"); },
    rows() {
      const search = this.search.trim().toLowerCase();
      return this.vehicles.filter((vehicle) =>
        [vehicle.plate, vehicle.vin, vehicle.brand, vehicle.model].join(" ").toLowerCase().includes(search)
      ).map((vehicle) => {
        const batteries = vehicle.batteries.length ? vehicle.batteries : [null];
        const groups = vehicle.fuel_type === "Electrico" ? batteries.map((battery, index) => ({
          title: batteries.length > 1 ? `Bateria de tração ${index + 1}` : "Bateria de tração", metrics: [
            this.metric(battery, "batteryPercentage", "Carga"), this.metric(battery, "autonomy", "Autonomia"),
          ],
        })) : [{ title: "Combustível", metrics: [
          this.metric(vehicle.fuel, "tankLiters", "Combustível no depósito"),
          this.metric(vehicle.fuel, "tankPercentage", "Nível do depósito"),
          this.metric(vehicle.fuel, "autonomy", "Autonomia"),
        ] }];
        const empty = "Sem informação disponível.";
        const hasReading = groups.some((group) => group.metrics.some((metric) => metric.value !== "-"));
        const status = {
          no_vin: empty,
          unavailable: `${empty} Não foi possível consultar a telemetria.`,
          missing: empty,
        }[vehicle.status] || (hasReading ? "" : empty);
        return { vehicle, groups, status };
      });
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) {
          this.today = getOnlyDate(new Date());
          this.showRental = false;
          this.rental = { registration: "", kms: "", rental_end_date: new Date() };
          this.actionError = "";
          this.actionNotice = "";
          this.loadVehicles();
          this.loadRouteDistance();
        } else {
          this.requestSequence++;
          this.loading = false;
          this.calculatingDistance = false;
        }
      },
    },
    date() {
      this.actionError = "";
      this.actionNotice = "";
      if (this.show) {
        this.loadVehicles();
        this.loadRouteDistance();
      }
    },
  },
  beforeDestroy() { this.requestSequence++; },
  deactivated() { this.visible = false; this.requestSequence++; this.loading = false; },
  methods: {
    formatTimestamp,
    availabilityLabel(status) {
      return {
        available: "Disponível.", reserved: "Reservado para esta data.",
        reserved_later: "Indisponível por reserva futura.", inactive: "Veículo inativo.",
        rental_expired: "Aluguer terminado para esta data.",
      }[status];
    },
    confirmCancel(vehicle) {
      if (!vehicle.can_cancel || !vehicle.reservation || this.loading || this.savingVehicleId !== null) return;
      const day = this.date;
      const name = vehicle.plate || vehicle.vin || "este veículo";
      const started = new Date(vehicle.reservation.start) <= new Date();
      this.$confirm.require({
        header: "Cancelar reserva",
        icon: "pi pi-question-circle",
        message: started
          ? `Pretende terminar a reserva de ${name} e atualizar os quilómetros pela telemetria?`
          : `Pretende cancelar a reserva de ${name}? Ainda não iniciou, por isso os quilómetros não serão alterados.`,
        acceptLabel: "Sim",
        rejectLabel: "Não",
        acceptClass: "p-button-danger",
        accept: () => { if (this.show && this.date === day) this.changeReservation(vehicle, true); },
      });
    },
    async changeReservation(vehicle, cancel) {
      if (!this.show || this.loading || this.savingVehicleId !== null
        || (cancel ? !vehicle.can_cancel || !vehicle.reservation : this.hasOpenReservation || !vehicle.can_reserve)) return;
      const sequence = this.requestSequence;
      const day = this.date;
      this.savingVehicleId = vehicle.vehicle_id;
      this.actionError = "";
      this.actionNotice = "";
      try {
        const result = cancel
          ? await FleetService.endVehicleReserve(vehicle.vehicle_id, vehicle.reservation.id)
          : await FleetService.reserveVehicle(vehicle.vehicle_id, day);
        if (this._isDestroyed) return;
        if (this.show && this.date === day && sequence === this.requestSequence) {
          const messages = {
            updated: "Reserva cancelada e quilómetros atualizados pela telemetria.",
            not_started: "Reserva cancelada. Como ainda não tinha iniciado, o registo de quilómetros não foi alterado.",
            missing: "Reserva cancelada. Sem leitura de quilómetros disponível; o registo de quilómetros não foi alterado.",
            unavailable: "Reserva cancelada. A telemetria está indisponível; o registo de quilómetros não foi alterado.",
            not_increased: "Reserva cancelada. A leitura não supera os quilómetros já registados; o registo foi mantido.",
            already_ended: "A reserva já estava cancelada.",
          };
          this.actionNotice = cancel
            ? messages[result.kms_status] || "Reserva cancelada."
            : "Veículo reservado. Pode cancelar manualmente; após o início, a reserva é encerrada automaticamente às 21h.";
          this.noticeSeverity = cancel && ["missing", "unavailable"].includes(result.kms_status) ? "warn" : "success";
          this.$emit(cancel ? "cancelled" : "reserved", result);
        }
      } catch (error) {
        if (this._isDestroyed) return;
        if (this.show && this.date === day && sequence === this.requestSequence) {
          const messages = {
            401: "A sessão expirou. Inicie sessão novamente.",
            403: "Não tem permissão para cancelar esta reserva.",
            404: "O veículo ou a reserva já não existem. A lista será atualizada.",
            409: "O estado do veículo foi alterado ou já não está disponível. A lista será atualizada.",
            422: "Escolha uma data válida, de hoje até 30 dias, para reservar.",
          };
          const response = error.response || {};
          const retry = "Atualize a lista antes de tentar novamente.";
          this.actionError = response.data && response.data.message === "User already has an open reservation."
            ? "Já tem uma reserva em aberto. Cancele-a antes de reservar outro veículo."
            : messages[response.status]
            || `Não foi possível confirmar ${cancel ? "o cancelamento" : "a reserva"}. ${retry}`;
        }
      } finally {
        if (!this._isDestroyed) {
          if (this.show) await this.loadVehicles();
          this.savingVehicleId = null;
        }
      }
    },
    async reserveRental() {
      if (!this.show || this.loading || this.savingVehicleId !== null || this.hasOpenReservation || this.date !== this.today) return;
      if (!(this.rental.rental_end_date instanceof Date) || !Number.isFinite(this.rental.rental_end_date.getTime())
        || getOnlyDate(this.rental.rental_end_date) < this.today) {
        this.actionError = "Selecione uma data de fim do aluguer de hoje ou posterior.";
        return;
      }
      const sequence = this.requestSequence;
      const day = this.date;
      this.savingVehicleId = "rental";
      this.actionError = "";
      this.actionNotice = "";
      try {
        const result = await FleetService.reserveRental({
          registration: this.rental.registration.trim(),
          kms: Number(this.rental.kms),
          rental_end_date: getOnlyDate(this.rental.rental_end_date),
        });
        if (this._isDestroyed || !this.show || this.date !== day || sequence !== this.requestSequence) return;
        this.showRental = false;
        this.actionNotice = "Veículo de aluguer registado e reservado.";
        this.noticeSeverity = "success";
        this.$emit("reserved", result);
      } catch (error) {
        if (this._isDestroyed || !this.show || this.date !== day || sequence !== this.requestSequence) return;
        const response = error.response || {};
        const data = response.data || {};
        this.actionError = response.status === 401
          ? "A sessão expirou. Inicie sessão novamente."
          : data.message === "User already has an open reservation."
            ? "Já tem uma reserva em aberto. Cancele-a antes de reservar outro veículo."
            : response.status === 422
              ? "Verifique a matrícula, os quilómetros e a data de fim do aluguer (hoje ou posterior)."
              : data.message || "Não foi possível registar e reservar o veículo de aluguer. Atualize a lista antes de tentar novamente.";
      } finally {
        if (!this._isDestroyed) {
          if (this.show) await this.loadVehicles();
          this.savingVehicleId = null;
        }
      }
    },
    metric(source, path, label) {
      const measure = reading(source, path);
      const raw = measure.value;
      const percentage = path.endsWith("Percentage");
      const valid = (typeof raw === "number" || typeof raw === "string" && raw.trim() !== "")
        && Number.isFinite(Number(raw)) && Number(raw) >= 0 && (!percentage || Number(raw) <= 100);
      return { path, label, value: formatReading(path, valid ? Number(raw) : null), timestamp: measure.timestamp };
    },
    async loadVehicles() {
      if (!this.show) return;
      const sequence = ++this.requestSequence;
      this.loading = true;
      this.error = "";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(this.date)) {
        this.error = "Selecione uma data na agenda para consultar os veículos.";
        this.loading = false;
        return;
      }
      try {
        const vehicles = await FleetService.listAvailableVehicles(this.date);
        if (sequence !== this.requestSequence) return;
        if (!Array.isArray(vehicles)) throw new Error("Invalid vehicle list format");
        const validVehicles = vehicles.filter((vehicle) =>
          vehicle && typeof vehicle === "object"
          && Number.isInteger(vehicle.vehicle_id) && vehicle.vehicle_id > 0
          && typeof vehicle.can_reserve === "boolean" && typeof vehicle.can_cancel === "boolean"
          && typeof vehicle.user_has_reservation === "boolean"
          && ["available", "reserved", "reserved_later", "inactive", "rental_expired"].includes(vehicle.availability_status)
          && (vehicle.reservation === null || (vehicle.reservation
            && Number.isInteger(vehicle.reservation.id) && typeof vehicle.reservation.start === "string"))
          && vehicle.fuel && typeof vehicle.fuel === "object" && !Array.isArray(vehicle.fuel)
          && Array.isArray(vehicle.batteries)
        );
        this.vehicles = validVehicles;
      } catch (error) {
        if (sequence === this.requestSequence) this.error = "Não foi possível carregar os veículos. Tente atualizar.";
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    async loadRouteDistance() {
      if (!this.show || !this.date) return;
      const sequence = ++this.distanceSequence;
      const user = this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.user;
      const base = getBaseLocation(user);
      this.totalDistanceKm = null;
      this.distanceIsEstimate = false;
      this.missingCoordsCount = 0;
      this.baseLocation = base;
      if (!base) return;

      this.calculatingDistance = true;
      try {
        const tasks = await getDayTasks(this.userId || (user && user.id), this.date);
        if (this._isDestroyed || sequence !== this.distanceSequence) return;
        const stops = tasks
          .map((task) => task.localization)
          .filter(hasCoords)
          .map((loc) => ({ lat: loc.latitude, lng: loc.longitude }));
        this.missingCoordsCount = tasks.length - stops.length;
        if (!stops.length) return;

        const result = await calculateRoundTrip(base, stops);
        if (this._isDestroyed || sequence !== this.distanceSequence) return;
        this.totalDistanceKm = result.km;
        this.distanceIsEstimate = result.isEstimate;
      } catch (err) {
        console.warn("Could not calculate day route distance for vehicles:", err);
      } finally {
        if (!this._isDestroyed && sequence === this.distanceSequence) {
          this.calculatingDistance = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.dialog-header-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.distance-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.25rem 0.65rem;
  border-radius: 4px;
}

.base-badge-note {
  margin-left: 0.35rem;
  font-weight: normal;
  opacity: 0.9;
}

.energy-toolbar, .energy-vehicle-heading, .energy-metric-value {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem;
}
.energy-search { flex: 1; min-width: 180px; }
.energy-search label { display: block; margin-bottom: 0.4rem; font-weight: 600; }
.energy-search input { width: 100%; }
.energy-note, .energy-vehicle small, .energy-vehicle-heading p { color: #667085; }
.energy-note { font-size: 0.85rem; line-height: 1.5; }
.availability-note { color: #28558c; font-size: 0.85rem; }
.availability-note.is-reserved { color: #82520e; font-weight: 600; }
.energy-list { display: flex; flex-direction: column; gap: 1rem; }
.energy-vehicle { border: 1px solid #e4e7ec; border-radius: 10px; padding: 1rem; }
.energy-vehicle-heading { margin-bottom: 0.75rem; }
.energy-vehicle-heading strong { font-size: 1.1rem; }
.energy-vehicle-heading p { margin: 0.3rem 0 0; }
.energy-vehicle-heading small { overflow-wrap: anywhere; }
.energy-groups { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr)); gap: 1rem; }
.energy-group { background: #f8fafc; border-radius: 8px; padding: 1rem; min-width: 0; }
.energy-group h3 { font-size: 1rem; margin: 0 0 0.75rem; }
.energy-metric + .energy-metric { margin-top: 0.75rem; }
.energy-metric small { display: block; margin-top: 0.3rem; font-size: 0.75rem; }
@media (max-width: 600px) { .energy-groups { grid-template-columns: minmax(0, 1fr); } }
</style>
